import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useAuth } from '../../contexts/AuthContext';

const SIDEBAR_COLLAPSED_KEY = 'mygmao.sidebar.collapsed';

/**
 * Layout applicatif (US-DASH-03) — coquille pour toutes les pages
 * authentifiées.
 *
 *  - Sidebar à gauche (w-64 sur desktop, repliable en w-16 ; overlay mobile).
 *  - Header en haut (h-16, fixed) avec logo tenant + cloche + dropdown profil.
 *  - Zone `<main>` qui hérite du padding (`p-4 sm:p-6`) et offset par
 *    `pt-16 lg:pl-{16|64}` selon l'état de la sidebar.
 *  - L'état "sidebar repliée" est persisté en `localStorage` pour qu'il
 *    survive aux rechargements / navigations.
 *
 * Pages publiques (Login, Landing, BootstrapAdmin, Forgot/Reset) ne passent
 * PAS par ce layout.
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  });

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  const isImpersonating = Boolean(user?.is_impersonated);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        isOpen={mobileOpen}
        isCollapsed={collapsed}
        onClose={() => setMobileOpen(false)}
        onToggleCollapse={() => setCollapsed((c) => !c)}
      />
      <Header onMobileMenu={() => setMobileOpen(true)} />
      <main
        className={`transition-all duration-300 ${collapsed ? 'lg:pl-16' : 'lg:pl-64'} ${
          isImpersonating ? 'pt-[6.25rem] sm:pt-[6rem]' : 'pt-16'
        }`}
      >
        <div className="p-4 sm:p-6">{children}</div>
      </main>
    </div>
  );
}
